import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment as env } from '@env/environment';

import { ApiService } from '@app/core/http/api.service';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    IOcbEnvironment,
    IOcbCUEnvironment,
    IOcbEnvironmentApiKey,
    IOcbApiDescriptionParameter,
    IOcbEmailTemplate,
    IOcbCUEmailTemplate,
    IOcbEnvironmentLog,
    OcbEnvironmentLogTypes,
} from '@app/core/models';

@Injectable()
export class EnvironmentsService extends ApiService {

    private environmentsUrl: string;

    private options = {
        headers: this.defaultHeaders,
        observe: 'body' as 'body',
    };

    constructor(httpClient: HttpClient) {
        super(httpClient);

        if (!env.api.endpoint) {
            throw new Error('Could not initialize the "environment" service, the proxyPath configuration is invalid.');
        }

        this.environmentsUrl = `${env.api.endpoint}/Environments`;
    }

    /**
     * Get the list of environments
     * @param includeDisabled {true} to return disabled environments
     */
    public getEnvironments(includeDisabled?: boolean): Observable<IOcbEnvironment[]> {

        let url = `${this.environmentsUrl}?`;
        if (includeDisabled) { url = `${url}IncludeDisabled=${includeDisabled}`; }

        return this.httpClient.get<IOcbEnvironment[]>(url, this.options);
    }

    /**
     * Create an environment
     * @param environment Environment to create
     */
    public addEnvironment(environment: IOcbCUEnvironment): Observable<IOcbEnvironment> {
        return this.httpClient.post<IOcbEnvironment>(this.environmentsUrl, environment, this.options);
    }

    /**
     * Get an environment
     * @param id Internal identifier of the environment
     */
    public getEnvironment(id?: string): Observable<IOcbEnvironment> {
        const url = `${this.environmentsUrl}/${id}`;
        return this.httpClient.get<IOcbEnvironment>(url, this.options);
    }

    /**
     * Update an environment
     * @param id Internal identifier of the environment to update
     * @param environment Environment to update
     */
    public updateEnvironment(id: string, environment: IOcbCUEnvironment): Observable<IOcbEnvironment> {
        const url = `${this.environmentsUrl}/${id}`;
        return this.httpClient.put<IOcbEnvironment>(url, environment, this.options);
    }

    /**
     * Delete an environment
     * @param id Internal identifier of the environment
     */
    public deleteEnvironment(id: string): Observable<void> {
        const url = `${this.environmentsUrl}/${id}`;
        return this.httpClient.delete<void>(url, this.options);
    }

    /**
     * Get the list of Api Keys of an environment
     * @param environmentId Internal identifier of the environment
     */
    public getEnvironmentApiKeys(environmentId: string): Observable<IOcbEnvironmentApiKey[]> {
        const url = `${this.environmentsUrl}/${environmentId}/ApiKeys`;
        return this.httpClient.get<IOcbEnvironmentApiKey[]>(url, this.options);
    }

    /**
     * Create an Api Key for an environment
     * @param environmentId Internal identifier of the environment
     * @param description Description of the Api Key
     */
    public addEnvironmentApiKey(environmentId: string, description: IOcbApiDescriptionParameter): Observable<IOcbEnvironmentApiKey> {
        const url = `${this.environmentsUrl}/${environmentId}/ApiKeys`;
        return this.httpClient.post<IOcbEnvironmentApiKey>(url, description, this.options);
    }

    /**
     * Get an Api Key of an environment
     * @param environmentId Internal identifier of the environment
     * @param apiKeyId Internal identifier of the Api Key
     */
    public getEnvironmentApiKey(environmentId: string, apiKeyId: string): Observable<IOcbEnvironmentApiKey> {
        const url = `${this.environmentsUrl}/${environmentId}/ApiKeys/${apiKeyId}`;
        return this.httpClient.get<IOcbEnvironmentApiKey>(url, this.options);
    }

    /**
     * Update the description of an Api Key of an environment
     * @param environmentId Internal identifier of the environment
     * @param apiKeyId Internal identifier of the Api Key
     * @param description New description
     */
    public updateEnvironmentApiKey(environmentId: string, apiKeyId: string, description: IOcbApiDescriptionParameter): Observable<void> {
        const url = `${this.environmentsUrl}/${environmentId}/ApiKeys/${apiKeyId}`;
        return this.httpClient.put<void>(url, description, this.options);
    }

    /**
     * Reset an Api Key of an environment
     * @param environmentId Internal identifier of the environment
     * @param apiKeyId Internal identifier of the Api Key
     */
    public resetEnvironmentApiKey(environmentId: string, apiKeyId: string): Observable<IOcbEnvironmentApiKey> {
        const url = `${this.environmentsUrl}/${environmentId}/ApiKeys/${apiKeyId}`;
        return this.httpClient.post<IOcbEnvironmentApiKey>(url, this.options);
    }

    /**
     * Delete an Api Key of an environment
     * @param environmentId Internal identifier of the environment
     * @param apiKeyId Internal identifier of the Api Key
     */
    public deleteEnvironmentApiKey(environmentId: string, apiKeyId: string): Observable<void> {
        const url = `${this.environmentsUrl}/${environmentId}/ApiKeys/${apiKeyId}`;
        return this.httpClient.delete<void>(url, this.options);
    }

    /**
     * Get an email template
     * @param environmentId Internal identifier of the environment
     * @param emailType Type of the email
     */
    public getEmailTemplate(environmentId: string, emailType: string): Observable<IOcbEmailTemplate> {
        const url = `${this.environmentsUrl}/${environmentId}/EmailTemplates/${emailType}`;
        return this.httpClient.get<IOcbEmailTemplate>(url, this.options);
    }

    /**
     * Update an email template
     * @param environmentId Internal identifier of the environment
     * @param emailType Type of the email
     * @param emailTemplate New template
     */
    public updateEmailTemplate(environmentId: string, emailType: string, emailTemplate: IOcbCUEmailTemplate): Observable<void> {
        const url = `${this.environmentsUrl}/${environmentId}/EmailTemplates/${emailType}`;
        return this.httpClient.put<void>(url, emailTemplate, this.options);
    }

    /**
     * Reset an email template
     * @param environmentId Internal identifier of the environment
     * @param emailType Type of the email
     */
    public resetEmailTemplate(environmentId: string, emailType: string): Observable<void> {
        const url = `${this.environmentsUrl}/${environmentId}/EmailTemplates/${emailType}/Reset`;
        return this.httpClient.post<void>(url, this.options);
    }

    // LOGS

    /**
     * Get an environment log
     * @param logId Internal identifier of the log
     */
    public getLog(logId: string): Observable<IOcbEnvironmentLog> {

        let url = `${this.environmentsUrl}/Logs/${logId}`;
        return this.httpClient.get<IOcbEnvironmentLog>(url, this.options).pipe(map(log => this.mapLogTypesToEnums(log)));
    }

    /**
     * Get the list of the logs of an environment
     * @param environmentId Internal identifier of the environment
     * @param utcDateFrom UTC Start date (time will be truncated)
     * @param utcDateTo UTC End date (time will be truncated)
     */
    public getLogs(environmentId: string, utcDateFrom?: string, utcDateTo?: string): Observable<IOcbEnvironmentLog[]> {

        let url = `${this.environmentsUrl}/${environmentId}/Logs?`;

        if (utcDateFrom) { url = `${url}UtcDateFrom=${utcDateFrom}&`; }
        if (utcDateTo) { url = `${url}UtcDateTo=${utcDateTo}`; }

        return this.httpClient.get<IOcbEnvironmentLog[]>(url, this.options).pipe(
            map(logs => logs.map(log => this.mapLogTypesToEnums(log))),
        );
    }

    // MAPPINGS

    /**
     * Map logs enum numbers to enum arrays.
     */
    private mapLogTypesToEnums(log: IOcbEnvironmentLog): IOcbEnvironmentLog {
        log.logTypeEnums = this.splitEnums(log.logType, OcbEnvironmentLogTypes);
        return log;
    }
}
